import React, { useEffect, useState } from 'react';
import { Button, Typography, Row, Select, App } from 'antd';
import { CustomerRule, MethodName, RuleFor } from '../../../../dto/admin-rule-models';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../../../authConfig';
import { CustomerRuleCard } from './customer-rule-card';
import { CustomerRuleForm } from './customer-rule-form';
import { getCustomerRules, postCustomerRules } from '../../../../services/admin-page-service';
import { SalesChannel } from '../../../../dto/model';
import { ExportOutlined, FilterOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { handleDownloadAllJson, handleJsonImport } from '../../../../utils/file-utils';

const { Title, Text } = Typography;

export const AdminCustomerRule = () => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [customerRules, setCustomerRules] = useState<CustomerRule[]>([]);
  const [displayRuleForm, shouldDisplayRuleForm] = useState<boolean>(false);
  const [displayAddButton, shouldDisplayAddButton] = useState<boolean>(true);
  const [outcomeRuleFilter, setOutcomeRuleFilter] = useState<RuleFor | undefined>(undefined);

  useEffect(() => {
    fetchCustomerRules();
  }, []);

  const fetchCustomerRules = async () => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const customerRules = await getCustomerRules(token.accessToken);
      if (customerRules.isSuccess) {
        setCustomerRules(customerRules.data!);
        return;
      }

      notification.error({
        message: 'Failed to load customer rules',
        description: customerRules.errors?.join(', ') || 'Loading customer rules failed.',
      });
    } catch (e: any) {
      notification.error({
        message: 'Failed to load customer rules',
        description: e.message || String(e),
      });
    }
  };

  const onSubmit = async (rule: CustomerRule) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await postCustomerRules(token.accessToken, [rule]);

      if (result.isSuccess) {
        notification.success({ message: 'Rule saved successfully' });
        await fetchCustomerRules();
        shouldDisplayRuleForm(false);
        shouldDisplayAddButton(true);
      } else {
        notification.error({
          message: 'Failed to save rule',
          description: result.errors?.join(', ') || 'Unknown error',
        });
      }
    } catch (e: any) {
      notification.error({
        message: 'Failed to save rule',
        description: e.message || String(e),
      });
    }
  };

  const onCancel = () => {
    const updatedRules = customerRules.filter((r) => r.id !== 0);
    shouldDisplayRuleForm(false);
    shouldDisplayAddButton(true);
    setCustomerRules(updatedRules);
  };

  const addCustomerRule = () => {
    const newRule = {
      id: 0,
      feId: crypto.randomUUID(),
      title: '',
      description: '',
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdBy: '',
      isDeleted: false,
      methodNames: [MethodName.NONE],
      salesChannels: [SalesChannel.MissingQueue],
      conditions: [],
      outcomes: [],
    };
    setCustomerRules([...customerRules, newRule]);
    shouldDisplayRuleForm(true);
    shouldDisplayAddButton(false);
  };

  const exportRules = async () => {
    await handleDownloadAllJson(customerRules, 'customer_rules_export');
  };

  const handleImport = () => {
    handleJsonImport<CustomerRule[]>(async (data) => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      await postCustomerRules(token.accessToken, data);
      const customerRules = await getCustomerRules(token.accessToken);
      if (customerRules.isSuccess) {
        setCustomerRules(customerRules.data!);
      }
    });
  };

  const onDuplicate = (duplicatedRule: CustomerRule) => {
    setCustomerRules([...customerRules, duplicatedRule]);
    shouldDisplayRuleForm(true);
    shouldDisplayAddButton(false);
  };

  const visualizeRuleForFilter = (ruleFor: RuleFor): string => {
    switch (ruleFor) {
      case RuleFor.OpportunitySync:
        return 'Opportunity Sync';
      case RuleFor.CalendarDisplay:
        return 'Calendar Display';
      case RuleFor.SlotBooking:
        return 'Slot Booking';
      case RuleFor.AgentsView:
        return 'Scripts';
      default:
        return ruleFor;
    }
  };
  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900, justifyContent: 'flex-start' }}>
      <Row style={{ marginBottom: '8px' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Customer Journey Rules
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: '5px' }}>
          When multiple rule execute against the same object, the order of execution is determined by the rule name. Please use the convention of 00, meaning 01, 02, 03, … before each rule name to
          ensure the order you want.
        </Text>
      </Row>
      {displayAddButton && (
        <Row justify="space-between" align="middle" style={{ marginBottom: '20px', marginTop: '20px' }}>
          {/* select on the far left */}
          <Select placeholder="Filter by Outcome" allowClear value={outcomeRuleFilter} onChange={setOutcomeRuleFilter} suffixIcon={<FilterOutlined />} style={{ width: 180 }}>
            {Object.entries(RuleFor).map(([key, val]) => (
              <Select.Option key={val} value={val}>
                {visualizeRuleForFilter(val)}
              </Select.Option>
            ))}
          </Select>

          {/* import/export/add buttons on the right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {process.env.REACT_APP_ENV !== 'production' && (
              <Button icon={<ImportOutlined />} onClick={handleImport}>
                Import Rules
              </Button>
            )}
            <Button icon={<ExportOutlined />} onClick={exportRules}>
              Export Rules
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={addCustomerRule}>
              Add Rule
            </Button>
          </div>
        </Row>
      )}
      {displayRuleForm && <CustomerRuleForm rule={customerRules.find((x) => +x.id === 0)!} onSubmit={onSubmit} onCancel={onCancel} />}
      {!displayRuleForm &&
        customerRules
          .filter((rule) => !rule.isDeleted)
          .filter((r) => !outcomeRuleFilter || r.outcomes.some((x) => x.ruleFor === outcomeRuleFilter))
          .map((rule) => <CustomerRuleCard key={rule.id} rule={rule} onSubmit={onSubmit} onDuplicate={onDuplicate} shouldDisplayAddButton={shouldDisplayAddButton} />)}
    </div>
  );
};
